import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { applyStimulsoftLanguage, resolveStimulsoftLanguage } from '@viilutalous/common';

const API_URL = import.meta.env.VITE_API_URL || '/api';

const LICENSE_KEY =
  '6vJhGtLLLz2GNviWmUTrhSqnOItdDwjBylQzQcAOiHluuVSveYqU53NOrvWCxTCYJkbgAejKQQLYkdcur0Tsu8hvXt' +
  'HG09yyxQXJJVzl29B0qGvS4V9bM/Yli7IK30oR0A0scKptUntTiLis7Brs/zoWbBZOdXdwiDHLUWD/QATDvbLWoxBC' +
  'uD/9vjkto9yV62qoY+XwbO7CNGmVlWDxGNjWOppVXscQAY4yxyreGep+R8rUoPQ1n7b0Lbr/ugnyw5P8LOxjRA6cW6' +
  'tY4JuzgyrbiFZ4xvjCXgtUzeT3RXDiBldfKlhYEwUlUqHKYsbjAXw2c6HgnrM0v5OZsCyLYeouLs3/HamYpdbLjWSz' +
  'GHUGLCibP7zRa9rAaTe91d6yO+we5AQX/igpdsYHGzzmKnoakR2bJEWIYLSk0LwG8jfsThVKmc/CrqBwetZks3piN9' +
  'zKhHLo6pjvZYNepjh2PiptE+7Dc4dTNHQcwWc2iPv6mIbOEQmJ4fZ01Aci9LLHXOVjTgl/lBCmF+rI6zmhJdsQ4oqs' +
  'RVqYubktSIOckVYmU9BwOenq3gUOKXMhAWOF';

export default function PublicInvoiceViewer() {
  const { i18n } = useTranslation();
  const language = resolveStimulsoftLanguage(i18n.language || i18n.resolvedLanguage);
  const messages = language === 'en'
    ? { invalidLink: 'Invalid link', templateError: 'Could not load the report template', loading: 'Loading...' }
    : { invalidLink: 'Virheellinen linkki', templateError: 'Raporttimallia ei voitu ladata', loading: 'Ladataan...' };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const viewerRef = useRef(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');

    if (!token) {
      setError(messages.invalidLink);
      setLoading(false);
      return;
    }

    let destroyed = false;

    const init = async () => {
      setLoading(true);
      setError(null);
      try {
        // Load Stimulsoft viewer
        await import('@viilu/reports-js/stimulsoft.reports.export.mjs');
        const mod = await import('@viilu/reports-js/stimulsoft.viewer.mjs');
        mod.Stimulsoft.Base.StiLicense.key = LICENSE_KEY;
        const Stimulsoft = mod.Stimulsoft;

        if (destroyed) return;

        // Load system invoice template
        const resp = await fetch(`${API_URL}/system-report-templates/invoice`);
        if (!resp.ok) throw new Error(messages.templateError);
        const templateJson = await resp.json();

        // Rewrite data source URL to use public endpoint with token
        if (templateJson.Dictionary?.Databases) {
          for (const db of Object.values(templateJson.Dictionary.Databases)) {
            if (db.PathData) {
              // Replace the authenticated URL with the public endpoint
              db.PathData = `${API_URL}/public/report-data/invoices?token=${encodeURIComponent(token)}`;
            }
          }
        }

        const report = new Stimulsoft.Report.StiReport();
        report.load(JSON.stringify(templateJson));

        if (destroyed) return;

        // Viewer options
        const viewerOptions = new Stimulsoft.Viewer.StiViewerOptions();
        viewerOptions.appearance.fullScreenMode = true;
        viewerOptions.appearance.theme = Stimulsoft.Viewer.StiViewerTheme.ViiluWhite;
        viewerOptions.toolbar.showDesignButton = false;
        viewerOptions.toolbar.showAboutButton = false;
        viewerOptions.appearance.scrollbarsMode = true;
        viewerOptions.appearance.showTooltipsHelp = false;
        viewerOptions.toolbar.showOpenButton = false;
        viewerOptions.toolbar.showBookmarkButton = false;
        viewerOptions.toolbar.viewMode = Stimulsoft.Viewer.StiWebViewMode.Continuous;

        const container = document.getElementById('public-invoice-viewer');
        if (!container) return;
        container.innerHTML = '';

        applyStimulsoftLanguage(Stimulsoft, language);
        const viewer = new Stimulsoft.Viewer.StiViewer(viewerOptions, 'StiViewer', false);
        viewerRef.current = viewer;
        viewer.report = report;
        viewer.renderHtml(container);
        setLoading(false);
      } catch (err) {
        console.error('Error initializing viewer:', err);
        if (!destroyed) {
          setError(err.message || 'Laskua ei voitu ladata');
          setLoading(false);
        }
      }
    };

    init();

    return () => {
      destroyed = true;
      if (viewerRef.current) {
        viewerRef.current.dispose();
        viewerRef.current = null;
      }
    };
  }, [language, messages.invalidLink, messages.templateError]);

  if (error) {
    return (
      <div style={{
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        height: '100vh', fontFamily: 'Arial, sans-serif',
      }}>
        <div style={{ textAlign: 'center', color: '#666' }}>
          <h2>Virhe</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ width: '100vw', height: '100vh', position: 'relative' }}>
      {loading && (
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
          display: 'flex', justifyContent: 'center', alignItems: 'center',
          zIndex: 10, backgroundColor: '#fff',
        }}>
          <div>{messages.loading}</div>
        </div>
      )}
      <div id="public-invoice-viewer" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }} />
    </div>
  );
}
